import React, { useState, useRef, useEffect } from 'react';
import { Button, Badge, Input } from '../../components/ui';
import { CheckCircle, AlertTriangle, Package, DollarSign, AlertCircle, RefreshCw, Edit2, Check, X, Loader2, Upload } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

const OverviewSection = ({ 
  validationStatus, 
  boxes, 
  trackingCodeMap, 
  selectedPlan, 
  shipmentItems,
  asinData,
  reloadAsinData,
  refreshBoxes,
  onComplete 
}) => {
  
  const [hoveredBox, setHoveredBox] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [editingPrice, setEditingPrice] = useState(null);
  const [tempPrice, setTempPrice] = useState('');
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const [isConvertingLabels, setIsConvertingLabels] = useState(false);
  const [isReloadingBoxes, setIsReloadingBoxes] = useState(false);

  // Helper function to get ASIN data
  const getAsinData = (asin) => {
    if (!asinData || !asin) return null;
    
    // Handle both array and object structures
    if (Array.isArray(asinData)) {
      return asinData.find(data => data.ASIN === asin);
    } else {
      return asinData[asin];
    }
  };

  // Calculate box contents and values
  const getBoxContents = (box) => {
    const contents = {};
    const items = [];
    let totalValue = 0;
    
    box.items?.forEach(item => {
      const asin = item.asin;
      const quantity = item.quantity || 0;
      const asinInfo = getAsinData(asin);
      
      // Use correct field names from ASIN data structure
      const price = asinInfo?.VALUE || 0; // VALUE is the price field in the database
      const title = asinInfo?.TITLE || asinInfo?.title || item.title || asin;
      const image = asinInfo?.IMAGE_URL || asinInfo?.image || '';
      
      items.push({
        asin,
        title,
        image,
        quantity,
        price,
        totalPrice: price * quantity
      });
      
      totalValue += price * quantity;
    });
    
    return { items, totalValue };
  };

  // Helper function to group identical boxes
  const groupIdenticalBoxes = (boxes) => {
    const grouped = [];
    let i = 0;
    
    while (i < boxes.length) {
      const currentBox = boxes[i];
      const currentContents = getBoxContents(currentBox);
      const group = [currentBox];
      
      // Look for consecutive identical boxes
      let j = i + 1;
      while (j < boxes.length) {
        const nextBox = boxes[j];
        const nextContents = getBoxContents(nextBox);
        
        // Check if boxes are identical (same items, quantities, and values)
        const sameItemCount = currentContents.items.length === nextContents.items.length;
        const sameValue = Math.abs(currentContents.totalValue - nextContents.totalValue) < 0.01;
        const sameItems = currentContents.items.every((item, idx) => {
          const nextItem = nextContents.items[idx];
          return nextItem && 
                 item.asin === nextItem.asin && 
                 item.quantity === nextItem.quantity;
        });
        
        if (sameItemCount && sameValue && sameItems && nextBox.box_number === currentBox.box_number + (j - i)) {
          group.push(nextBox);
          j++;
        } else {
          break;
        }
      }
      
      grouped.push(group);
      i = j;
    }
    
    return grouped;
  };

  // Get tracking code for box
  const getTrackingCode = (boxNumber) => {
    return trackingCodeMap[boxNumber] || 'Not Found';
  };

  // Improved hover handlers
  const handleBoxHover = (group, event) => {
    // Clear any existing timeouts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Calculate position immediately
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 320; // Fixed width for tooltip
    
    let x = rect.left + rect.width / 2;
    let y = rect.bottom + 8;
    
    // Adjust horizontal position if tooltip would go off screen
    if (x + tooltipWidth / 2 > viewportWidth - 20) {
      x = viewportWidth - tooltipWidth - 20;
    } else if (x - tooltipWidth / 2 < 20) {
      x = 20;
    } else {
      x = x - tooltipWidth / 2;
    }
    
    // Adjust vertical position if tooltip would go off screen
    if (y + 400 > window.innerHeight) {
      y = rect.top - 8; // Show above the box instead
    }

    setTooltipPosition({ x, y });
    setHoveredBox(group);
    setIsTooltipVisible(true);
  };

  const handleBoxLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      if (!isTooltipVisible) return;
      setIsTooltipVisible(false);
      setHoveredBox(null);
    }, 150);
  };

  const handleTooltipEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
  };

  const handleTooltipLeave = () => {
    setIsTooltipVisible(false);
    setHoveredBox(null);
  };

  // Price editing functions with database updates
  const startEditingPrice = (index, currentPrice) => {
    setEditingPrice(index);
    setTempPrice(currentPrice.toString());
  };

  const savePrice = async (index, asin) => {
    const newPrice = parseFloat(tempPrice) || 0;
    
    try {
      // Update database first
      const response = await fetch(`${API_URL}/api/update_price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ asin, value: newPrice })
      });

      if (!response.ok) {
        throw new Error('Failed to update price in database');
      }

      // Reload ASIN data to get fresh data and recalculate box values
      await reloadAsinData();
      
      setEditingPrice(null);
      setTempPrice('');
      
    } catch (error) {
      console.error('Error updating price:', error);
      // Optionally show an error message to the user
    }
  };

  const cancelEdit = () => {
    setEditingPrice(null);
    setTempPrice('');
  };

  const refreshPrice = async (asin) => {
    try {
      // Force reload of ASIN data which will refresh prices
      await reloadAsinData();
    } catch (error) {
      console.error('Error refreshing price:', error);
    }
  };

  // Handle prior notice label conversion to 4x6
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsConvertingLabels(true);

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        const payload = {
          name: file.name,
          type: file.type,
          base64Data: base64Data,
          sizeBytes: file.size,
        };

        try {
          const response = await fetch(`${API_URL}/proxy-retool`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            const result = await response.json();
            const base64PDF = result.response;

            // Convert base64 PDF back to Blob and download it
            const byteCharacters = atob(base64PDF);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const downloadUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'PN-LABELS.pdf';
            link.click();
          } else {
            console.error('Error processing PDF:', response.statusText);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
        } finally {
          setIsConvertingLabels(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return (
    <div className="space-y-4 relative">
      {/* Status indicators and action buttons */}
      <div className="flex items-center justify-between p-3 bg-white rounded border border-slate-200">
        <div className="flex items-center space-x-4">
          {validationStatus?.fda && (
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-700">FDA Complete</span>
            </div>
          )}
          {validationStatus?.address && (
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-700">Address Validated</span>
            </div>
          )}
          {validationStatus?.labels && (
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-700">Labels Purchased</span>
            </div>
          )}
        </div>
        
        {/* Critical Alerts */}
        {boxes && boxes.some(box => getBoxContents(box).totalValue > 800) && (
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium">High Value Boxes Detected</span>
          </div>
        )}
        
        <div className="flex items-center space-x-2">
          {/* Convert Labels Button */}
          <Button
            variant="outline"
            disabled={isConvertingLabels}
            className="h-8 px-4 text-xs relative"
          >
            {isConvertingLabels ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-3 h-3 mr-1" />
                Convert PN Labels
              </>
            )}
            <input
              type="file"
              hidden
              accept="application/pdf"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </Button>
          
          <Button 
            onClick={onComplete}
            className="h-8 px-4 text-xs"
          >
            Continue to Next Step
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Items List */}
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-2">Shipment Items</h3>
          <div className="bg-white rounded border border-slate-200">
                      <div className="max-h-80 overflow-y-auto">
            {(() => {
              // Filter items based on selected plan
              const filteredItems = shipmentItems && selectedPlan && selectedPlan.items ? 
                shipmentItems.filter(item => 
                  selectedPlan.items.some(planItem => planItem.msku === item.item.merchant_sku)
                ) : [];
              
              return filteredItems.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {filteredItems.map((item, index) => {
                    // Handle different possible data structures
                    const itemData = item.item || item;
                    const asin = itemData.asin || item.asin;
                    const quantity = itemData.quantity || item.quantity || 0;
                    const title = itemData.title || item.title || '';
                    
                    const asinInfo = getAsinData(asin);
                    const price = asinInfo?.VALUE || 0;
                    
                    // Try multiple image sources
                    let image = null;
                    if (asinInfo?.IMAGE_URL) image = asinInfo.IMAGE_URL;
                    else if (asinInfo?.image) image = asinInfo.image;
                    else if (itemData.images && itemData.images[0]?.large_url) image = itemData.images[0].large_url;
                    else if (itemData.images && itemData.images[0]?.medium_url) image = itemData.images[0].medium_url;
                    else if (itemData.images && itemData.images[0]?.small_url) image = itemData.images[0].small_url;
                    
                    const displayTitle = asinInfo?.TITLE || asinInfo?.title || title || asin;
                    
                    return (
                      <div key={index} className="p-3 flex items-center space-x-3">
                        {/* Product Image */}
                        <div className="w-16 h-16 flex-shrink-0">
                          {image ? (
                            <img 
                              src={image} 
                              alt={displayTitle}
                              className="w-16 h-16 object-cover rounded border border-slate-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center ${image ? 'hidden' : 'flex'}`}>
                            <Package className="w-8 h-8 text-slate-400" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {displayTitle}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-slate-500 mt-1">
                            <span>ASIN: {asin}</span>
                            <span>•</span>
                            <span>Qty: {quantity}</span>
                          </div>
                        </div>
                        
                        <div className="text-right flex items-center space-x-2">
                          <div>
                            {editingPrice === index ? (
                              <div className="flex items-center space-x-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={tempPrice}
                                  onChange={(e) => setTempPrice(e.target.value)}
                                  className="w-16 h-6 text-xs text-center p-1"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      savePrice(index, asin);
                                    } else if (e.key === 'Escape') {
                                      cancelEdit();
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                                  onClick={() => savePrice(index, asin)}
                                >
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                  onClick={cancelEdit}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div className={`text-sm font-semibold ${
                                  price > 0 ? 'text-green-600' : 'text-red-500'
                                }`}>
                                  ${price.toFixed(2)}
                                </div>
                                <div className="text-xs text-slate-500">
                                  Total: ${(price * quantity).toFixed(2)}
                                </div>
                              </>
                            )}
                          </div>
                          {editingPrice !== index && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 w-6 p-0"
                                onClick={() => startEditingPrice(index, price)}
                                title="Edit price"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 w-6 p-0"
                                onClick={() => refreshPrice(asin)}
                                title="Refresh price"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                              ) : (
                  <div className="p-6 text-center text-sm text-slate-500">
                    {!selectedPlan || !selectedPlan.items ? 
                      'No plan selected' : 
                      shipmentItems && shipmentItems.length > 0 ? 
                        'No items match the selected plan' : 
                        'No items found'
                    }
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Boxes Grid */}
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-2">Shipping Boxes</h3>
          <div className="bg-white rounded border border-slate-200 p-4">
            {boxes && boxes.length > 0 ? (
              <div className="grid grid-cols-5 gap-4">
                {groupIdenticalBoxes(boxes).map((group, index) => {
                  const firstBox = group[0];
                  const contents = getBoxContents(firstBox);
                  const trackingCode = getTrackingCode(firstBox.box_number);
                  const individualValue = contents.totalValue; // Value per box
                  const totalValue = individualValue * group.length; // Total value for all boxes in group
                  const isOverLimit = individualValue > 800; // Check limit per individual box
                  const hasIssues = isOverLimit || trackingCode === 'Not Found' || totalValue === 0;
                  
                  const boxLabel = group.length > 1 
                    ? `${group[0].box_number}-${group[group.length - 1].box_number}`
                    : `${firstBox.box_number}`;
                  
                  return (
                    <div 
                      key={index} 
                      className={`relative w-24 h-24 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md ${
                        hasIssues 
                          ? 'border-red-400 bg-gradient-to-br from-red-50 to-red-100' 
                          : 'border-green-400 bg-gradient-to-br from-green-50 to-green-100'
                      }`}
                      onMouseEnter={(e) => handleBoxHover(group, e)}
                      onMouseLeave={handleBoxLeave}
                    >
                      {/* Box Number/Range */}
                      <div className="flex flex-col h-full justify-center items-center p-2">
                        <div className={`text-lg font-bold leading-none ${
                          hasIssues ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {boxLabel}
                        </div>
                        <div className={`text-xs font-semibold leading-none mt-1 ${
                          hasIssues ? 'text-red-600' : 'text-green-600'
                        }`}>
                          ${individualValue.toFixed(0)}
                        </div>
                        
                        {/* SKU and Unit counts */}
                        <div className="text-xs text-slate-600 leading-none mt-1 text-center">
                          <div>{contents.items.length} SKUs</div>
                          <div>{contents.items.reduce((sum, item) => sum + item.quantity, 0) * group.length} Units</div>
                        </div>
                        
                        {/* Count indicator for grouped boxes */}
                        {group.length > 1 && (
                          <div className="absolute top-1 right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {group.length}
                          </div>
                        )}
                      </div>

                      {/* Critical Alert Icon */}
                      {isOverLimit && (
                        <div className="absolute top-1 left-1 bg-red-500 rounded-full p-1">
                          <AlertTriangle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-600 mb-2">No boxes found for this plan</div>
                <div className="text-xs text-slate-500 mb-3">
                  Plan ID: {selectedPlan?.inbound_plan_id || selectedPlan?.id}<br/>
                  This could mean the plan has no boxes yet, or there was an issue loading the data.
                </div>
                <Button
                  onClick={async () => {
                    console.log('Reloading boxes for plan:', selectedPlan?.id);
                    if (refreshBoxes && selectedPlan) {
                      setIsReloadingBoxes(true);
                      try {
                        const result = await refreshBoxes(selectedPlan);
                        console.log('Boxes reloaded:', result?.length || 0);
                        if (!result || result.length === 0) {
                          console.warn('Still no boxes found after manual reload');
                        }
                      } catch (error) {
                        console.error('Error reloading boxes:', error);
                      } finally {
                        setIsReloadingBoxes(false);
                      }
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={isReloadingBoxes}
                >
                  {isReloadingBoxes ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Reload Boxes
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Improved Interactive Tooltip Portal */}
      {isTooltipVisible && hoveredBox && (
        <div 
          ref={tooltipRef}
          className="fixed bg-white border border-slate-300 rounded-lg shadow-2xl p-4 w-80 z-[9999] pointer-events-auto"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            maxHeight: '400px',
            overflowY: 'auto'
          }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        >
          {(() => {
            const firstBox = hoveredBox[0];
            const contents = getBoxContents(firstBox);
            const trackingCode = getTrackingCode(firstBox.box_number);
            const individualValue = contents.totalValue; // Value per box
            const totalValue = individualValue * hoveredBox.length; // Total value for all boxes
            const isOverLimit = individualValue > 800; // Check limit per individual box
            
            const boxLabel = hoveredBox.length > 1 
              ? `${hoveredBox[0].box_number}-${hoveredBox[hoveredBox.length - 1].box_number}`
              : `${firstBox.box_number}`;
            
            return (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
                  <span className="font-semibold text-slate-900">
                    {hoveredBox.length > 1 ? `Boxes ${boxLabel} (${hoveredBox.length} boxes)` : `Box ${boxLabel}`}
                  </span>
                  <div className="text-right">
                    <span className={`text-sm font-medium ${
                      isOverLimit ? 'text-red-600' : individualValue > 0 ? 'text-green-600' : 'text-slate-500'
                    }`}>
                      ${individualValue.toFixed(2)}
                      {hoveredBox.length > 1 && (
                        <div className="text-xs text-slate-500">
                          Total: ${totalValue.toFixed(2)}
                        </div>
                      )}
                    </span>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3 text-center text-xs">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="font-medium text-slate-900">{contents.items.length}</div>
                    <div className="text-slate-500">SKUs</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="font-medium text-slate-900">
                      {contents.items.reduce((sum, item) => sum + item.quantity, 0) * hoveredBox.length}
                    </div>
                    <div className="text-slate-500">Units</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="font-medium text-slate-900">{Math.round(firstBox.weight * hoveredBox.length)} kg</div>
                    <div className="text-slate-500">Weight</div>
                  </div>
                </div>

                {/* Dimensions & Tracking */}
                <div className="space-y-1 mb-3 text-xs text-slate-600 bg-slate-50 rounded p-2">
                  <div><strong>Dimensions:</strong> {Math.round(firstBox.length)}×{Math.round(firstBox.width)}×{Math.round(firstBox.height)} cm</div>
                  <div><strong>Tracking:</strong> <span className="font-mono text-blue-600">{trackingCode}</span></div>
                  {hoveredBox.length > 1 && (
                    <div><strong>Quantity:</strong> {hoveredBox.length} identical boxes</div>
                  )}
                </div>

                {/* Items */}
                {contents.items.length > 0 ? (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-xs font-medium text-slate-700 mb-2">Contents per box:</div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {contents.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs bg-slate-50 rounded p-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">
                              {item.title}
                            </div>
                            <div className="text-slate-500 truncate">
                              {item.asin}
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <div className="font-medium text-slate-900">
                              {item.quantity}×
                            </div>
                            <div className={`text-xs ${
                              item.price > 0 ? 'text-green-600' : 'text-red-500'
                            }`}>
                              ${item.totalPrice.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-xs text-slate-500 text-center py-2">
                      No items found in this box
                    </div>
                  </div>
                )}

                {/* Warning */}
                {isOverLimit && (
                  <div className="mt-3 pt-2 border-t border-red-200 bg-red-50 rounded p-2">
                    <div className="text-xs text-red-700 font-medium flex items-center">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Exceeds $800 limit - May require special handling
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default OverviewSection; 